import axios from 'axios'
import Cookies from 'js-cookie'
import type { AxiosError, AxiosRequestConfig, Axios } from 'axios'
import { triggerGlobalLogout } from '../lib/authContext'

const upStage = process.env.NEXT_PUBLIC_UP_STAGE

const prodStage = ['production', 'prod'].includes(upStage ?? '')

const API_URL = prodStage
  ? 'https://api.shipper.space'
  : 'https://api-staging.shipper.space'

export const DEFAULT_TIMEOUT = 21000

const defaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
}

const axiosClient: Axios = axios.create({
  baseURL: API_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: defaultHeaders,
})

// Flag to prevent redirect loops
let isRedirecting = false

// Auth redirect interceptor - handles 401 responses by triggering logout
axiosClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      typeof error?.response?.config.url === 'string' &&
      !error?.response?.config.url.endsWith('users/sessions') &&
      error?.response?.status === 401 &&
      !isRedirecting
    ) {
      isRedirecting = true

      console.warn(
        '[auth] 401 on %s — triggering logout',
        error?.response?.config.url,
      )
      // Use the global logout function to properly clear auth context
      triggerGlobalLogout()

      return Promise.reject(error)
    }
    return Promise.reject(error)
  },
)

export function getAuthenticatedClient(authToken: string | null): Axios {
  // Keep redirect protection behavior but avoid mutating shared axios defaults.
  // Auth header is attached per-request by callers/client() to prevent races.
  if (authToken) {
    isRedirecting = false
  }
  return axiosClient
}

export interface RequestConfig<TData = unknown> {
  baseURL?: string
  url?: string
  method: 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE' | 'OPTIONS' | 'HEAD'
  params?: unknown
  data?: TData | FormData
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream'
  signal?: AbortSignal
  validateStatus?: (status: number) => boolean
  headers?: AxiosRequestConfig['headers']
  paramsSerializer?: AxiosRequestConfig['paramsSerializer']
  timeout?: number
}

export interface ResponseConfig<TData = unknown> {
  data: TData
  status: number
  statusText: string
  headers?: Record<string, any>
}

export type ResponseError<TError = unknown> = AxiosError<TError>
export type ResponseErrorConfig<TError = unknown> = AxiosError<TError>
export type Client = <TResponseData, _TError = unknown, TRequestData = unknown>(
  config: RequestConfig<TRequestData>,
) => Promise<ResponseConfig<TResponseData>>

export async function client<
  TData = unknown,
  _TError = unknown,
  TRequest = unknown,
>(config: RequestConfig<TRequest>): Promise<ResponseConfig<TData>> {
  const authToken = Cookies.get('authToken') || null
  const axiosClient = getAuthenticatedClient(authToken)

  const response = await axiosClient.request<TData>({
    ...config,
    url: config.url,
    method: config.method,
    params: config.params,
    data: config.data,
    headers: {
      ...config.headers,
      ...(authToken ? { Authorization: authToken } : {}),
    },
    signal: config.signal,
    timeout: config.timeout,
  })

  return {
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  }
}

// Export as both default and named 'fetch' for Kubb compatibility
export default client
export { client as fetch }
